// components/StreakUsersDrawer.tsx
import React from 'react';
import {
  Drawer,
  Table,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Empty
} from 'antd';
import {
  UserOutlined,
  RiseOutlined,
  TeamOutlined,
  TrophyOutlined,
  CalendarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface StreakUser {
  username: string;
  userId: string;
  accountName: string;
  totalLoads?: number;
  operationCount?: number;
  lastOperationDate?: Date | null;
  firstOperationDate?: Date | null;
  inactiveDays?: number;
  returnDate?: Date | null;
  lastActivityBeforeReturn?: Date | null;
}

interface StreakUsersDrawerProps {
  visible: boolean;
  onClose: () => void;
  type: 'superActive' | 'returning' | null;
  users: StreakUser[];
  threshold?: number;
  theme?: 'dark' | 'light';
}

export const StreakUsersDrawer: React.FC<StreakUsersDrawerProps> = ({
  visible,
  onClose,
  type,
  users,
  threshold,
  theme = 'light'
}) => {
  const isSuperActive = type === 'superActive';

  const config = isSuperActive ? {
    title: `⭐ Usuarios Súper Activos (${threshold}+ cargas)`,
    description: 'Usuarios con alta actividad de cargas',
    color: '#eb2f96',
    gradient: 'linear-gradient(135deg, #ff85c0 0%, #eb2f96 100%)',
    icon: RiseOutlined
  } : {
    title: `🔄 Usuarios que Regresan`,
    description: 'Usuarios que volvieron después de inactividad',
    color: '#13c2c2',
    gradient: 'linear-gradient(135deg, #5cdbd3 0%, #13c2c2 100%)',
    icon: TeamOutlined
  };

  const superActiveColumns = [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
      render: (text: string, record: StreakUser) => (
        <Space>
          <UserOutlined style={{ color: config.color }} />
          <div>
            <Text strong style={{ color: theme === 'dark' ? '#fff' : '#000' }}>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.accountName}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Cargas Totales',
      dataIndex: 'totalLoads',
      key: 'totalLoads',
      sorter: (a: StreakUser, b: StreakUser) => (a.totalLoads || 0) - (b.totalLoads || 0),
      render: (value: number) => (
        <Tag color="green" icon={<TrophyOutlined />}>
          {value} cargas
        </Tag>
      )
    },
    {
      title: 'Operaciones',
      dataIndex: 'operationCount',
      key: 'operationCount',
      render: (value: number) => (
        <Text style={{ color: theme === 'dark' ? '#fff' : '#000' }}>{value}</Text>
      )
    },
    {
      title: 'Primera Operación',
      dataIndex: 'firstOperationDate',
      key: 'firstOperationDate',
      render: (date: Date | null) => (
        date ? (
          <Space>
            <CalendarOutlined style={{ color: '#1890ff' }} />
            <Text style={{ color: theme === 'dark' ? '#fff' : '#000' }}>
              {dayjs(date).format('DD/MM/YYYY HH:mm')}
            </Text>
          </Space>
        ) : <Text type="secondary">-</Text>
      )
    },
    {
      title: 'Última Operación',
      dataIndex: 'lastOperationDate',
      key: 'lastOperationDate',
      render: (date: Date | null) => (
        date ? (
          <Space>
            <ClockCircleOutlined style={{ color: '#52c41a' }} />
            <Text style={{ color: theme === 'dark' ? '#fff' : '#000' }}>
              {dayjs(date).format('DD/MM/YYYY HH:mm')}
            </Text>
          </Space>
        ) : <Text type="secondary">-</Text>
      )
    }
  ];

  const returningColumns = [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
      render: (text: string, record: StreakUser) => (
        <Space>
          <UserOutlined style={{ color: config.color }} />
          <div>
            <Text strong style={{ color: theme === 'dark' ? '#fff' : '#000' }}>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.accountName}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Días de Inactividad',
      dataIndex: 'inactiveDays',
      key: 'inactiveDays',
      sorter: (a: StreakUser, b: StreakUser) => (a.inactiveDays || 0) - (b.inactiveDays || 0),
      render: (value: number) => (
        <Tag color="orange" icon={<ClockCircleOutlined />}>
          {value} días
        </Tag>
      )
    },
    {
      title: 'Última Actividad (antes del gap)',
      dataIndex: 'lastActivityBeforeReturn',
      key: 'lastActivityBeforeReturn',
      render: (date: Date | null) => (
        date ? (
          <Space>
            <CalendarOutlined style={{ color: '#faad14' }} />
            <Text style={{ color: theme === 'dark' ? '#fff' : '#000' }}>
              {dayjs(date).format('DD/MM/YYYY')}
            </Text>
          </Space>
        ) : <Text type="secondary">-</Text>
      )
    },
    {
      title: 'Fecha de Regreso',
      dataIndex: 'returnDate',
      key: 'returnDate',
      render: (date: Date | null) => (
        date ? (
          <Space>
            <CalendarOutlined style={{ color: '#52c41a' }} />
            <Text style={{ color: theme === 'dark' ? '#fff' : '#000' }}>
              {dayjs(date).format('DD/MM/YYYY')}
            </Text>
          </Space>
        ) : <Text type="secondary">-</Text>
      )
    }
  ];

  return (
    <Drawer
      title={
        <div style={{
          background: config.gradient,
          margin: -24,
          marginBottom: 24,
          padding: '24px',
          borderRadius: '8px 8px 0 0'
        }}>
          <Row align="middle" justify="space-between">
            <Col>
              <Space direction="vertical" size={0}>
                <Title level={4} style={{ margin: 0, color: '#fff' }}>
                  {config.title}
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                  {config.description}
                </Text>
              </Space>
            </Col>
            <Col>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '12px',
                borderRadius: '50%',
                display: 'inline-flex'
              }}>
                {React.createElement(config.icon, {
                  style: { fontSize: 28, color: '#fff' }
                })}
              </div>
            </Col>
          </Row>
        </div>
      }
      placement="right"
      size="large"
      onClose={onClose}
      open={visible}
      styles={{
        body: {
          padding: 0,
          background: theme === 'dark' ? '#1a1d23' : '#f0f2f5'
        }
      }}
    >
      <div style={{ padding: '0 24px 24px' }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <div style={{
              background: theme === 'dark' ? '#23272f' : '#fff',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: theme === 'dark'
                ? '0 2px 8px rgba(0,0,0,0.32)'
                : '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text strong style={{ fontSize: 16, color: theme === 'dark' ? '#fff' : '#000' }}>
                  📊 Resumen
                </Text>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="secondary">Total de usuarios:</Text>
                    <br />
                    <Text strong style={{ fontSize: 20, color: config.color }}>
                      {users.length}
                    </Text>
                  </Col>
                  {isSuperActive && (
                    <Col span={12}>
                      <Text type="secondary">Umbral actual:</Text>
                      <br />
                      <Text strong style={{ fontSize: 20, color: config.color }}>
                        {threshold}+ cargas
                      </Text>
                    </Col>
                  )}
                </Row>
              </Space>
            </div>
          </Col>
        </Row>

        {users.length > 0 ? (
          <Table
            columns={isSuperActive ? superActiveColumns : returningColumns}
            dataSource={users}
            rowKey="userId"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} usuarios`
            }}
            scroll={{ x: 800 }}
            style={{
              background: theme === 'dark' ? '#23272f' : '#fff',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          />
        ) : (
          <Empty
            description="No hay usuarios en esta categoría"
            style={{
              background: theme === 'dark' ? '#23272f' : '#fff',
              padding: '48px',
              borderRadius: '12px'
            }}
          />
        )}
      </div>
    </Drawer>
  );
};
